import Section from "@bmi-digital/components/section";
import { graphql, navigate } from "gatsby";
import React, { useEffect, useMemo, useState } from "react";
import { isDefined } from "@bmi/utils";
import type { Training } from "@bmi/elasticsearch-types";
import Breadcrumbs from "../../components/Breadcrumbs";
import Page from "../../components/Page";
import ProgressIndicator from "../../components/ProgressIndicator";
import Scrim from "../../components/Scrim";
import { updateBreadcrumbTitleFromPayload } from "../../utils/breadcrumbUtils";
import { getPathWithCountryCode } from "../../utils/path";
import {
  getIntouchTrainingPageUrl,
  getIntouchTrainingTargetUrl
} from "../../utils/intouchTrainingUrl";
import { useConfig } from "../../contexts/ConfigProvider";
import TrainingRegistrationForm from "./components/TrainingRegistrationForm";
import TrainingRegistrationHeader from "./components/TrainingRegistrationHeader";
import WarningDialog from "./components/WarningDialog";
import { useRegistration } from "./hooks/useRegistration";
import { useShowWarningModal } from "./hooks/useShowWarningModal";
import { FormStatus } from "./types";
import type { BreadcrumbItem } from "../../types/breadcrumb";
import type { PageData } from "../../components/Page";
import type { TrainingRegistrationPageProps } from "./types";

const useBreadcrumbs = (
  payloadTrainingRegistrationPage:
    | TrainingRegistrationPageProps["data"]["payloadTrainingRegistrationPage"]
    | null,
  payloadTrainingListerPage: TrainingRegistrationPageProps["data"]["payloadTrainingListerPage"],
  training?: Training
): BreadcrumbItem[] => {
  return useMemo(() => {
    if (!payloadTrainingRegistrationPage) {
      return [];
    }

    const registrationPageBreadcrumbs = updateBreadcrumbTitleFromPayload(
      payloadTrainingRegistrationPage.breadcrumbs,
      payloadTrainingRegistrationPage.breadcrumbTitle
    );
    const currentPageBreadcrumb =
      registrationPageBreadcrumbs[registrationPageBreadcrumbs.length - 1];

    const trainingPageBreadcrumb: BreadcrumbItem | undefined = training && {
      id: training.courseId.toString(),
      label: training.courseName,
      slug: `t/${training.courseSlug}`
    };

    const trainingListerPageBreadcrumb: BreadcrumbItem | undefined =
      payloadTrainingListerPage
        ? {
            ...payloadTrainingListerPage.breadcrumbs[
              payloadTrainingListerPage.breadcrumbs.length - 1
            ],
            slug: payloadTrainingListerPage.path
          }
        : undefined;

    return [
      trainingListerPageBreadcrumb,
      trainingPageBreadcrumb,
      currentPageBreadcrumb
    ].filter(isDefined);
  }, [training, payloadTrainingRegistrationPage, payloadTrainingListerPage]);
};

const getPageData = (breadcrumbs: BreadcrumbItem[], path: string): PageData => {
  return {
    breadcrumbs,
    signupBlock: null,
    meta: null,
    path
  };
};

const TrainingRegistrationPage = ({ data }: TrainingRegistrationPageProps) => {
  const [formStatus, setFormStatus] = useState(FormStatus.Initialized);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const registrationPage = data.payloadTrainingRegistrationPage;
  const path = registrationPage?.path ?? "";
  const { training, loading } = useRegistration();
  const { blockedLocation, closeWarningDialog } = useShowWarningModal({
    formStatus,
    isSubmitting
  });

  const breadcrumbs = useBreadcrumbs(
    registrationPage,
    data.payloadTrainingListerPage,
    training
  );
  const { isPreviewMode } = useConfig();

  const pageData = getPageData(breadcrumbs, path);

  useEffect(() => {
    if (isPreviewMode) {
      return;
    }

    if (loading) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const trainingCode = params.get("trainingCode")?.trim() ?? "";
    const session = params.get("session")?.trim();
    const destination =
      trainingCode && session
        ? getIntouchTrainingTargetUrl(
            training?.courseId ?? trainingCode,
            session
          ) ?? getIntouchTrainingPageUrl()
        : null;

    if (!destination) {
      navigate(getPathWithCountryCode(data.payloadSite.countryCode, "404"));
      return;
    }

    window.location.assign(destination);
  }, [data.payloadSite.countryCode, isPreviewMode, loading, training]);

  return (
    <Page
      title={registrationPage?.breadcrumbTitle || ""}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
    >
      {blockedLocation && (
        <WarningDialog
          blockedUrl={blockedLocation.href}
          closeDialog={closeWarningDialog}
        />
      )}
      {loading && (
        <Scrim theme="light">
          <ProgressIndicator theme="light" />
        </Scrim>
      )}
      <Section backgroundColor="white" data-testid="breadcrumbs-section-top">
        <Breadcrumbs
          data={breadcrumbs}
          concatenateUrls={false}
          data-testid="breadcrumbs-top"
        />
      </Section>
      {training && <TrainingRegistrationHeader training={training} />}
      {!loading && isPreviewMode && registrationPage && (
        <TrainingRegistrationForm
          formData={registrationPage}
          trainingDetailsPageUrl={getPathWithCountryCode(
            data.payloadSite.countryCode,
            `/t/${training?.courseSlug}`
          )}
          courseCode={training?.courseCode}
          training={training}
          setFormStatus={setFormStatus}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />
      )}
      <Section backgroundColor="white" data-testid="breadcrumbs-section-bottom">
        <Breadcrumbs
          data={breadcrumbs}
          concatenateUrls={false}
          data-testid="breadcrumbs-bottom"
        />
      </Section>
    </Page>
  );
};

export default TrainingRegistrationPage;

export const pageQuery = graphql`
  query TrainingRegistrationPageById {
    payloadTrainingRegistrationPage {
      ...PayloadTrainingRegistrationPageFragment
      breadcrumbTitle
      title
      salutationTitle
      salutationMale
      salutationFemale
      firstName
      lastName
      email
      companyName
      position
      customerNumber
      city
      street
      postalCode
      phoneNumber
      competentChamber
      recipient
      emailSubject
      competentChamberLabel
      bmiSystemPartnerClubTitle
      isMemberOfBmiLabel
      isNotMemberOfBmiLabel
      discoverySourceTitle
      discoverySourceBrochure
      discoverySourceFieldService
      discoverySourceWebsite
      discoverySourceFacebook
      discoverySourceInstagram
      discoverySourceXing
      discoverySourceLinkedin
      discoverySourceOther
      discoverySourceSpecifyOther
      comment
      extraParticipantTitle
      extraParticipantSubtitle
      addParticipantsButton
      newParticipantTitle
      removeParticipantButton
      newParticipantFirstName
      newParticipantLastName
      consentText
      termsOfUse
      registerButton
    }
    payloadTrainingListerPage {
      path
      breadcrumbs {
        ...BreadcrumbItemFragment
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
